import { useEffect, useRef, useState } from "react";
import {
  fetchInstrumen,
  fetchTarifMatriks,
  fetchDokter,
  createInstrumen,
  updateInstrumen,
  deleteInstrumen,
  createDokter,
  updateDokter,
  deleteDokter,
  updateMatriks,
  exportMasterExcel,
  importMasterExcel,
  formatApiError,
} from "../lib/api";
import { formatRupiah } from "../lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Table as TableIcon,
  Stethoscope,
  PencilSimple,
  Trash,
  Plus,
  Check,
  X,
  DownloadSimple,
  UploadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const KELAS = ["KELAS 1", "KELAS 2", "KELAS 3", "KELAS VIP", "KELAS VVIP"];
const KOMPONEN_OPTIONS = [
  "Dokter Operator",
  "Dokter Asisten",
  "Asisten Operator",
  "Dokter Anestesi",
  "Sewa Kamar Operasi",
  "Sewa Kamar Recovery Room",
  "Sewa Kamar RR",
  "Sewa Peralatan Operasi",
  "Sewa Alat Khusus",
  "Jasa Paramedis",
  "Box Bayi Baru Lahir",
  "Visite Dokter Spesialis",
  "Dokter Anak",
];
const MATRIKS_COLS = [
  "Jasa Dokter Operator",
  "Jasa Asisten Operator",
  "Jasa Dokter Anastesi",
  "Sewa Kamar Operasi",
  "Sewa RR",
  "Sewa Peralatan Operasi",
  "Sewa Alat Khusus",
  "Jasa Paramedis",
];

// ============================================================================
// Instrumen Editor
// ============================================================================
function InstrumenEditor() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // detail key being edited
  const [draft, setDraft] = useState(null);
  const [filter, setFilter] = useState("");
  const [penjamin, setPenjamin] = useState("UMUM");

  const load = () => fetchInstrumen(penjamin).then(setRows);
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [penjamin]);

  const startEdit = (r) => {
    setEditing(r.detail);
    setDraft({ ...r, tarif: { ...r.tarif } });
  };

  const startAdd = () => {
    setEditing("__new__");
    setDraft({
      nama_instrumen: "Dokter Operator",
      detail: "",
      kategori: "",
      tarif: { "KELAS 1": 0, "KELAS 2": 0, "KELAS 3": 0, "KELAS VIP": 0, "KELAS VVIP": 0 },
    });
  };

  const cancel = () => {
    setEditing(null);
    setDraft(null);
  };

  const save = async () => {
    try {
      if (!draft.detail.trim() || !draft.kategori.trim()) {
        toast.error("Detail dan Kategori wajib diisi.");
        return;
      }
      if (editing === "__new__") {
        await createInstrumen(draft, penjamin);
        toast.success("Instrumen Umum/Asuransi ditambahkan.");
      } else {
        await updateInstrumen(editing, draft, penjamin);
        toast.success("Instrumen Umum/Asuransi diupdate.");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (detail) => {
    if (!window.confirm(`Hapus "${detail}"?`)) return;
    try {
      await deleteInstrumen(detail, penjamin);
      toast.success("Dihapus.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = rows.filter(
    (r) =>
      !filter ||
      r.detail.toLowerCase().includes(filter.toLowerCase()) ||
      r.kategori.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <div className="bg-white border border-slate-200 rounded shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-slate-100 gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 bg-slate-100 rounded p-0.5" data-testid="penjamin-tabs">
            {[
              { v: "UMUM", l: "Umum/Asuransi" },
              { v: "BPJSTK", l: "BPJSTK" },
            ].map((p) => (
              <button
                key={p.v}
                type="button"
                onClick={() => setPenjamin(p.v)}
                className={`px-3 py-1.5 text-xs font-bold rounded transition-colors ${
                  penjamin === p.v
                    ? "bg-white text-[#0A3F9A] shadow-sm"
                    : "text-slate-600 hover:bg-white/50"
                }`}
                data-testid={`penjamin-tab-${p.v.toLowerCase()}`}
              >
                {p.l}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Cari nama instrumen / kategori..."
            className="bg-white border border-slate-300 rounded px-3 py-2 text-sm w-72 focus:ring-2 focus:ring-[#0A3F9A]"
            data-testid="input-filter-instrumen"
          />
        </div>
        <button
          onClick={startAdd}
          className="bg-[#0A3F9A] text-white px-3 py-2 rounded text-sm font-medium flex items-center gap-2"
          data-testid="btn-add-instrumen"
        >
          <Plus size={14} /> Tambah Instrumen Umum/Asuransi
        </button>
      </div>

      {editing === "__new__" && draft && (
        <EditRow draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} isNew />
      )}

      <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className="bg-slate-50 text-slate-600 font-semibold text-left p-2 border-b text-xs">
                Komponen
              </th>
              <th className="bg-slate-50 text-slate-600 font-semibold text-left p-2 border-b text-xs">
                Detail
              </th>
              <th className="bg-slate-50 text-slate-600 font-semibold text-left p-2 border-b text-xs">
                Kategori
              </th>
              {KELAS.map((k) => (
                <th
                  key={k}
                  className="bg-slate-50 text-slate-600 font-semibold text-right p-2 border-b text-xs"
                >
                  {k}
                </th>
              ))}
              <th className="bg-slate-50 text-slate-600 font-semibold text-right p-2 border-b text-xs">
                Aksi
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const isEditing = editing === r.detail;
              if (isEditing) {
                return (
                  <EditRow
                    key={r.detail}
                    draft={draft}
                    setDraft={setDraft}
                    onSave={save}
                    onCancel={cancel}
                  />
                );
              }
              return (
                <tr key={r.detail || i} className="hover:bg-slate-50" data-testid={`row-instrumen-${i}`}>
                  <td className="p-2 border-b border-slate-100 text-xs whitespace-nowrap">
                    {r.nama_instrumen}
                  </td>
                  <td className="p-2 border-b border-slate-100 text-xs">{r.detail}</td>
                  <td className="p-2 border-b border-slate-100 text-xs whitespace-nowrap">
                    {r.kategori}
                  </td>
                  {KELAS.map((k) => (
                    <td
                      key={k}
                      className="p-2 border-b border-slate-100 text-right tabular-nums text-xs whitespace-nowrap"
                    >
                      {formatRupiah(r.tarif?.[k])}
                    </td>
                  ))}
                  <td className="p-2 border-b border-slate-100 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => startEdit(r)}
                        className="text-slate-600 hover:text-[#0A3F9A] p-1"
                        data-testid={`btn-edit-instrumen-${i}`}
                      >
                        <PencilSimple size={14} weight="duotone" />
                      </button>
                      <button
                        onClick={() => remove(r.detail)}
                        className="text-slate-600 hover:text-red-600 p-1"
                        data-testid={`btn-delete-instrumen-${i}`}
                      >
                        <Trash size={14} weight="duotone" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditRow({ draft, setDraft, onSave, onCancel, isNew }) {
  return (
    <tr className="bg-yellow-50">
      <td className="p-2 border-b border-slate-100">
        <select
          value={draft.nama_instrumen}
          onChange={(e) => setDraft({ ...draft, nama_instrumen: e.target.value })}
          className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
          data-testid="edit-instrumen-nama"
        >
          {KOMPONEN_OPTIONS.map((k) => (
            <option key={k}>{k}</option>
          ))}
        </select>
      </td>
      <td className="p-2 border-b border-slate-100">
        <input
          type="text"
          value={draft.detail}
          onChange={(e) => setDraft({ ...draft, detail: e.target.value })}
          placeholder="Mis. Dokter Operator Operasi Besar Bedah Umum"
          className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
          disabled={!isNew}
          data-testid="edit-instrumen-detail"
        />
      </td>
      <td className="p-2 border-b border-slate-100">
        <input
          type="text"
          value={draft.kategori}
          onChange={(e) => setDraft({ ...draft, kategori: e.target.value })}
          className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
          data-testid="edit-instrumen-kategori"
        />
      </td>
      {KELAS.map((k) => (
        <td key={k} className="p-2 border-b border-slate-100">
          <input
            type="number"
            value={draft.tarif[k] || 0}
            onChange={(e) =>
              setDraft({
                ...draft,
                tarif: { ...draft.tarif, [k]: Number(e.target.value) },
              })
            }
            className="w-28 border border-slate-300 rounded px-2 py-1 text-xs text-right tabular-nums"
            data-testid={`edit-instrumen-tarif-${k}`}
          />
        </td>
      ))}
      <td className="p-2 border-b border-slate-100 text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={onSave}
            className="text-white bg-[#057A55] hover:bg-[#046148] p-1 rounded"
            data-testid="btn-save-instrumen"
          >
            <Check size={14} weight="bold" />
          </button>
          <button
            onClick={onCancel}
            className="text-slate-600 hover:bg-slate-200 p-1 rounded"
            data-testid="btn-cancel-instrumen"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ============================================================================
// Dokter Editor
// ============================================================================
function DokterEditor() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [ksmList, setKsmList] = useState([]);

  const load = () =>
    fetchDokter().then((d) => {
      setRows(d);
      setKsmList(Array.from(new Set(d.map((x) => x.ksm))).sort());
    });

  useEffect(() => {
    load();
  }, []);

  const startEdit = (r) => {
    setEditing(r.nama);
    setDraft({ ...r });
  };
  const startAdd = () => {
    setEditing("__new__");
    setDraft({ nama: "", ksm: ksmList[0] || "" });
  };
  const cancel = () => {
    setEditing(null);
    setDraft(null);
  };

  const save = async () => {
    if (!draft.nama.trim() || !draft.ksm.trim()) {
      toast.error("Nama dan KSM wajib diisi.");
      return;
    }
    try {
      if (editing === "__new__") {
        await createDokter(draft);
        toast.success("Dokter ditambahkan.");
      } else {
        await updateDokter(editing, draft);
        toast.success("Dokter diupdate.");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (nama) => {
    if (!window.confirm(`Hapus dokter "${nama}"?`)) return;
    try {
      await deleteDokter(nama);
      toast.success("Dihapus.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded shadow-sm">
      <div className="flex items-center justify-end p-4 border-b border-slate-100">
        <button
          onClick={startAdd}
          className="bg-[#0A3F9A] text-white px-3 py-2 rounded text-sm font-medium flex items-center gap-2"
          data-testid="btn-add-dokter"
        >
          <Plus size={14} /> Tambah Dokter
        </button>
      </div>
      <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0">
            <tr>
              <th className="bg-slate-50 text-slate-600 font-semibold text-left p-2 border-b text-xs">
                Nama
              </th>
              <th className="bg-slate-50 text-slate-600 font-semibold text-left p-2 border-b text-xs">
                KSM
              </th>
              <th className="bg-slate-50 text-slate-600 font-semibold text-right p-2 border-b text-xs">
                Aksi
              </th>
            </tr>
          </thead>
          <tbody>
            {editing === "__new__" && draft && (
              <tr className="bg-yellow-50">
                <td className="p-2 border-b">
                  <input
                    type="text"
                    value={draft.nama}
                    onChange={(e) => setDraft({ ...draft, nama: e.target.value })}
                    placeholder="dr.Nama Lengkap, Sp.X"
                    className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
                    data-testid="edit-dokter-nama"
                  />
                </td>
                <td className="p-2 border-b">
                  <input
                    type="text"
                    value={draft.ksm}
                    list="ksm-list"
                    onChange={(e) => setDraft({ ...draft, ksm: e.target.value })}
                    className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
                    data-testid="edit-dokter-ksm"
                  />
                  <datalist id="ksm-list">
                    {ksmList.map((k) => (
                      <option key={k} value={k} />
                    ))}
                  </datalist>
                </td>
                <td className="p-2 border-b text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={save} className="text-white bg-[#057A55] p-1 rounded" data-testid="btn-save-dokter">
                      <Check size={14} weight="bold" />
                    </button>
                    <button onClick={cancel} className="text-slate-600 p-1 rounded">
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                </td>
              </tr>
            )}
            {rows.map((r, i) => {
              const isEditing = editing === r.nama;
              if (isEditing) {
                return (
                  <tr key={r.nama} className="bg-yellow-50">
                    <td className="p-2 border-b text-xs">{r.nama}</td>
                    <td className="p-2 border-b">
                      <input
                        type="text"
                        value={draft.ksm}
                        list="ksm-list"
                        onChange={(e) => setDraft({ ...draft, ksm: e.target.value })}
                        className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
                      />
                    </td>
                    <td className="p-2 border-b text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={save} className="text-white bg-[#057A55] p-1 rounded">
                          <Check size={14} weight="bold" />
                        </button>
                        <button onClick={cancel} className="text-slate-600 p-1 rounded">
                          <X size={14} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={`${r.nama}-${i}`} className="hover:bg-slate-50" data-testid={`row-dokter-${i}`}>
                  <td className="p-2 border-b text-sm">{r.nama}</td>
                  <td className="p-2 border-b text-xs text-slate-600">{r.ksm}</td>
                  <td className="p-2 border-b text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => startEdit(r)}
                        className="text-slate-600 hover:text-[#0A3F9A] p-1"
                        data-testid={`btn-edit-dokter-${i}`}
                      >
                        <PencilSimple size={14} weight="duotone" />
                      </button>
                      <button
                        onClick={() => remove(r.nama)}
                        className="text-slate-600 hover:text-red-600 p-1"
                        data-testid={`btn-delete-dokter-${i}`}
                      >
                        <Trash size={14} weight="duotone" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Matriks Editor (edit-in-place)
// ============================================================================
function MatriksEditor() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = () => fetchTarifMatriks().then(setRows);
  useEffect(() => {
    load();
  }, []);

  const startEdit = (r) => {
    setEditing(r.kondisi);
    setDraft({ ...r, multipliers: { ...r.multipliers } });
  };

  const cancel = () => {
    setEditing(null);
    setDraft(null);
  };

  const save = async () => {
    try {
      await updateMatriks(editing, draft);
      toast.success("Matriks diupdate.");
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="bg-slate-50 text-slate-600 font-semibold text-left p-2 border-b text-xs">
                Kondisi
              </th>
              {MATRIKS_COLS.map((c) => (
                <th
                  key={c}
                  className="bg-slate-50 text-slate-600 font-semibold text-center p-2 border-b text-xs"
                >
                  {c}
                </th>
              ))}
              <th className="bg-slate-50 text-slate-600 font-semibold text-right p-2 border-b text-xs">
                Aksi
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const isEditing = editing === r.kondisi;
              return (
                <tr
                  key={r.kondisi}
                  className={isEditing ? "bg-yellow-50" : "hover:bg-slate-50"}
                  data-testid={`row-matriks-${i}`}
                >
                  <td className="p-2 border-b text-xs font-medium whitespace-nowrap">
                    {r.kondisi}
                  </td>
                  {MATRIKS_COLS.map((c) => (
                    <td key={c} className="p-2 border-b text-center text-xs">
                      {isEditing ? (
                        <input
                          type="text"
                          value={draft.multipliers?.[c] ?? ""}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              multipliers: {
                                ...draft.multipliers,
                                [c]: isNaN(Number(e.target.value))
                                  ? e.target.value
                                  : Number(e.target.value),
                              },
                            })
                          }
                          className="w-20 border border-slate-300 rounded px-2 py-1 text-xs text-center tabular-nums"
                          data-testid={`edit-matriks-${i}-${c}`}
                        />
                      ) : (
                        <span className="tabular-nums">
                          {r.multipliers?.[c] ?? "-"}
                        </span>
                      )}
                    </td>
                  ))}
                  <td className="p-2 border-b text-right">
                    {isEditing ? (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={save} className="text-white bg-[#057A55] p-1 rounded">
                          <Check size={14} weight="bold" />
                        </button>
                        <button onClick={cancel} className="text-slate-600 p-1 rounded">
                          <X size={14} weight="bold" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEdit(r)}
                        className="text-slate-600 hover:text-[#0A3F9A] p-1"
                        data-testid={`btn-edit-matriks-${i}`}
                      >
                        <PencilSimple size={14} weight="duotone" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Page
// ============================================================================
export default function AdminMasterPage() {
  const importRef = useRef(null);
  const [reloadKey, setReloadKey] = useState(0);

  const handleExport = async () => {
    try {
      await exportMasterExcel();
      toast.success("Backup Excel diunduh.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("Import akan MENGGANTI seluruh data master (Instrumen Umum/Asuransi + Matriks + Dokter). Lanjut?")) {
      e.target.value = "";
      return;
    }
    try {
      const res = await importMasterExcel(file);
      const counts = res.inserted || {};
      toast.success(
        `Import sukses. Instrumen: ${counts.instrumen || 0}, Matriks: ${counts.matriks || 0}, Dokter: ${counts.dokter || 0}`,
      );
      setReloadKey((k) => k + 1);
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-white border border-slate-200 rounded shadow-sm p-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
            Master Data Editor
          </div>
          <div className="text-xl md:text-2xl font-semibold text-slate-900 flex items-center gap-2">
            Kelola Tarif Instrumen Umum/Asuransi, Matriks & Dokter
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Hanya admin yang bisa mengakses halaman ini.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleExport}
            className="bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded hover:bg-slate-50 text-sm font-medium flex items-center gap-2"
            data-testid="btn-export-excel-master"
          >
            <DownloadSimple size={14} weight="duotone" /> Backup Excel
          </button>
          <input
            ref={importRef}
            type="file"
            accept=".xlsx"
            onChange={handleImport}
            className="hidden"
            data-testid="input-import-excel-master"
          />
          <button
            onClick={() => importRef.current?.click()}
            className="bg-[#D97706] text-white px-3 py-2 rounded hover:bg-[#b96305] text-sm font-medium flex items-center gap-2"
            data-testid="btn-import-excel-master"
          >
            <UploadSimple size={14} weight="duotone" /> Restore dari Excel
          </button>
        </div>
      </div>

      <Tabs defaultValue="instrumen" data-testid="tabs-admin">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="instrumen" data-testid="tab-instrumen">
            <TableIcon size={14} className="mr-1.5" weight="duotone" /> Instrumen Umum/Asuransi
          </TabsTrigger>
          <TabsTrigger value="matriks" data-testid="tab-matriks">
            <TableIcon size={14} className="mr-1.5" weight="duotone" /> Tarif Matriks
          </TabsTrigger>
          <TabsTrigger value="dokter" data-testid="tab-dokter">
            <Stethoscope size={14} className="mr-1.5" weight="duotone" /> Dokter
          </TabsTrigger>
        </TabsList>

        <TabsContent value="instrumen" className="mt-4">
          <InstrumenEditor key={`i-${reloadKey}`} />
        </TabsContent>
        <TabsContent value="matriks" className="mt-4">
          <MatriksEditor key={`m-${reloadKey}`} />
        </TabsContent>
        <TabsContent value="dokter" className="mt-4">
          <DokterEditor key={`d-${reloadKey}`} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
